import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', path: '/' },
  { label: '电影分类', path: '/movie-category' },
  { label: '关于我们', path: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) {
      navigate('/search');
      return;
    }
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-950/95 backdrop-blur-md border-b border-background-800'
          : 'bg-gradient-to-b from-background-950/80 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 md:gap-3 group shrink-0">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 flex items-center justify-center text-background-50 text-lg md:text-xl">
            <i className="ri-film-line"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-black text-foreground-50 text-base md:text-lg tracking-wide whitespace-nowrap">
              善良的小姨子
            </span>
            <span className="hidden sm:block text-[10px] md:text-xs text-foreground-400 tracking-[0.25em] font-display">
              FREE MOVIE SITE
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => {
            const active =
              item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? 'text-primary-400 bg-primary-500/10'
                    : 'text-foreground-300 hover:text-foreground-50 hover:bg-background-800/60'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={handleSearch} className="hidden lg:flex items-center">
            <div className="flex items-center bg-background-800/70 border border-background-700 focus-within:border-primary-500 rounded-md transition-colors">
              <span className="w-9 h-9 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影…"
                className="bg-transparent outline-none text-sm text-foreground-100 placeholder:text-foreground-500 w-36 xl:w-48 pr-3 py-1.5"
              />
            </div>
          </form>

          <Link
            to="/search"
            className="lg:hidden w-9 h-9 rounded-md flex items-center justify-center text-foreground-200 hover:bg-background-800/70 transition-colors cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>

          <Link
            to="/movie-detail/1"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-fill"></i>
            <span className="hidden md:inline">立即观看</span>
            <span className="md:hidden">观看</span>
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="md:hidden w-9 h-9 rounded-md flex items-center justify-center text-foreground-100 hover:bg-background-800/70 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-800 bg-background-950/98 px-4 py-4 animate-fade-in">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="flex items-center bg-background-800/70 border border-background-700 focus-within:border-primary-500 rounded-md">
              <span className="w-9 h-9 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影…"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-100 placeholder:text-foreground-500 py-2 pr-3"
              />
            </div>
          </form>
          <div className="flex flex-col">
            {navItems.map((item) => {
              const active =
                item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                    active ? 'text-primary-400 bg-primary-500/10' : 'text-foreground-200 hover:bg-background-800/60'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              to="/search"
              className="px-3 py-3 rounded-md text-sm font-medium text-foreground-200 hover:bg-background-800/60 cursor-pointer"
            >
              搜索影片
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}